// The trading journal's data: trades (GET/POST/PATCH/DELETE /v1/trades), the statistics for a
// period (/v1/trades/stats, computed by the API) and tradebook import.
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';

export const SETUPS = [
  { id: 'order-block', label: 'Order block' }, { id: 'sweep', label: 'Liquidity sweep' }, { id: 'breakout', label: 'Breakout' },
  { id: 'momentum', label: 'Momentum' }, { id: 'pullback', label: 'Pullback' }, { id: 'reversal', label: 'Reversal' }, { id: 'other', label: 'Other' },
] as const;
export const TAGS = [
  { id: 'followed-plan', label: 'Followed plan', good: true }, { id: 'disciplined', label: 'Disciplined', good: true },
  { id: 'early-exit', label: 'Early exit', good: false }, { id: 'late-entry', label: 'Late entry', good: false },
  { id: 'moved-stop', label: 'Moved stop', good: false }, { id: 'oversized', label: 'Oversized', good: false },
  { id: 'fomo', label: 'FOMO', good: false }, { id: 'revenge', label: 'Revenge', good: false },
] as const;
export const SESSION_PARTS: Record<string, string> = {
  'pre-open': 'Pre-open', opening: 'Opening 09:15', prime: 'Prime 09:20–11:30', slow: 'Midday 11:30–13:30',
  afternoon: 'Afternoon 13:30–15:00', late: 'Late 15:00+', 'after-hours': 'After hours',
};
export const setupLabel = (id: string) => SETUPS.find((s) => s.id === id)?.label ?? (id === 'untagged' ? 'No setup' : id);
export const tagLabel = (id: string) => TAGS.find((t) => t.id === id)?.label ?? id;

/** What MoneyHQ showed when the trade was logged from a stock page. */
export interface PlanSnapshot {
  time?: string; zone?: string; state?: string; z?: number; stop?: number; t1?: number; t2?: number; rr?: number | null;
  score?: number; side?: 'bull' | 'bear'; eventRisk?: string | null; session?: string;
}

export interface Trade {
  id: string; symbol: string; segment: 'EQ' | 'FO'; side: 'long' | 'short'; qty: number;
  entry: number; exit: number | null; entryAt: string; exitAt: string | null; fees: number;
  setup: string; tags: string[]; stop: number | null; target: number | null; note: string;
  plan: PlanSnapshot | null; source: 'manual' | 'zerodha-csv';
  net: number; gross: number; r: number | null; session: string;
}
export type TradeInput = Omit<Trade, 'id' | 'source' | 'net' | 'gross' | 'r' | 'session'>;

export interface Bucket { key: string; trades: number; wins: number; net: number; winRate: number; avgR: number | null }
export interface DayPnL { date: string; net: number; trades: number }
export interface Stats {
  trades: number; open: number; wins: number; losses: number; net: number; gross: number; fees: number; winRate: number;
  profitFactor: number | null; expectancy: number; expectancyR: number | null; avgWin: number; avgLoss: number; payoff: number | null;
  maxDrawdown: number; maxDrawdownDate: string; bestDay: DayPnL | null; worstDay: DayPnL | null;
  streak: { kind: '' | 'win' | 'loss'; n: number }; longestWin: number; longestLoss: number; greenDays: number; redDays: number;
  equity: { date: string; cum: number; drawdown: number }[]; days: DayPnL[]; months: Bucket[];
  bySetup: Bucket[]; byTag: Bucket[]; byWeekday: Bucket[]; byTime: Bucket[]; bySymbol: Bucket[]; bySide: Bucket[]; plan: Bucket[];
}

const err = async (r: Response) => (await r.json().catch(() => ({}))).error ?? 'Something went wrong';

export function useTrades(range: { from: string; to: string }) {
  const [trades, setTrades] = useState<Trade[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const reload = useCallback(async () => {
    const q = new URLSearchParams({ from: range.from, to: range.to });
    const [a, b] = await Promise.all([api('/v1/trades'), api(`/v1/trades/stats?${q}`)]);
    setTrades(a.ok ? await a.json() : []);
    setStats(b.ok ? await b.json() : null);
  }, [range.from, range.to]);
  useEffect(() => { void reload(); }, [reload]);

  const save = async (t: TradeInput, id?: string): Promise<string | null> => {
    const r = await api(id ? `/v1/trades/${id}` : '/v1/trades', { method: id ? 'PATCH' : 'POST', body: JSON.stringify(t) });
    if (!r.ok) return err(r);
    await reload();
    return null;
  };
  const remove = async (id: string) => { const r = await api(`/v1/trades/${id}`, { method: 'DELETE' }); if (r.ok) await reload(); return r.ok; };
  const importCsv = async (text: string): Promise<{ added: number; skipped: number; open: number; warnings: string[] } | string> => {
    const r = await api('/v1/trades/import', { method: 'POST', body: text, headers: { 'content-type': 'text/csv' } });
    if (!r.ok) return err(r);
    const out = await r.json();
    await reload();
    return out;
  };
  return { trades, stats, reload, save, remove, importCsv };
}

/** Logs a trade from anywhere (the stock page's trade plan). */
export async function logTrade(t: TradeInput): Promise<string | null> {
  const r = await api('/v1/trades', { method: 'POST', body: JSON.stringify(t) });
  return r.ok ? null : err(r);
}
